import { useMemo } from "react";
import { cx } from "@/utils/cx";
import { TimelineItem } from "./timeline-item";

const MIN_GAP_PX = 16;
const MAX_GAP_PX = 150;

function isSameDay(a: Date, b: Date): boolean {
    return (
        a.getFullYear() === b.getFullYear() &&
        a.getMonth() === b.getMonth() &&
        a.getDate() === b.getDate()
    );
}

function isSameHour(a: Date, b: Date): boolean {
    return isSameDay(a, b) && a.getHours() === b.getHours();
}

function formatDateLabel(date: Date, kind: "date" | "time" | "datetime"): string {
    if (kind === "date") {
        return date.toLocaleDateString(undefined, {
            day: "numeric",
            month: "short",
            year: "numeric",
        });
    }
    if (kind === "time") {
        return date.toLocaleTimeString(undefined, {
            hour: "2-digit",
            minute: "2-digit",
        });
    }
    return date.toLocaleString(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}

/**
 * For each item, compute optional label: show when day or hour changes (or first item).
 */
function computeDateLabels(items: TimelineEntry[]): (string | undefined)[] {
    if (items.length === 0) return [];
    return items.map((entry, index) => {
        if (index === 0) {
            return formatDateLabel(entry.date, "datetime");
        }
        const prev = items[index - 1].date;
        const curr = entry.date;
        if (!isSameDay(prev, curr)) {
            return formatDateLabel(curr, "date");
        }
        if (!isSameHour(prev, curr)) {
            return formatDateLabel(curr, "time");
        }
        return undefined;
    });
}

export interface TimelineEntry {
    id: string;
    date: Date;
    content: React.ReactNode;
    position: "left" | "right";
}

export interface TimelineProps {
    items: TimelineEntry[];
    order?: "asc" | "desc";
    className?: string;
}

/**
 * Compute margin-bottom (px) for each item based on time distance to next.
 * Uses max consecutive delta in list to normalize; gap in [MIN_GAP_PX, MAX_GAP_PX].
 */
function computeGaps(dates: Date[]): number[] {
    if (dates.length <= 1) return dates.length === 1 ? [MIN_GAP_PX] : [];
    const gaps: number[] = [];
    let maxDeltaMs = 0;
    for (let i = 0; i < dates.length - 1; i++) {
        const deltaMs = Math.abs(dates[i].getTime() - dates[i + 1].getTime());
        if (deltaMs > maxDeltaMs) maxDeltaMs = deltaMs;
    }
    for (let i = 0; i < dates.length - 1; i++) {
        const deltaMs = Math.abs(dates[i].getTime() - dates[i + 1].getTime());
        const normalized = maxDeltaMs > 0 ? deltaMs / maxDeltaMs : 0;
        const gap = Math.round(MIN_GAP_PX + (MAX_GAP_PX - MIN_GAP_PX) * normalized);
        gaps.push(Math.min(MAX_GAP_PX, Math.max(MIN_GAP_PX, gap)));
    }
    gaps.push(MIN_GAP_PX);
    return gaps;
}

export function Timeline({ items, order: _order = "desc", className }: TimelineProps) {
    const gaps = useMemo(() => {
        const dates = items.map((i) => i.date);
        return computeGaps(dates);
    }, [items]);

    const dateLabels = useMemo(() => computeDateLabels(items), [items]);

    if (items.length === 0) {
        return (
            <div className={cx("rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary text-tertiary", className)}>
                Nessun elemento in timeline.
            </div>
        );
    }

    return (
        <div className={cx("relative", className)}>
            {/* Vertical line behind dots */}
            <div
                aria-hidden
                className="absolute left-1/2 top-0 bottom-0 w-px -translate-x-px bg-secondary"
            />
            <div className="relative flex flex-col">
                {items.map((entry, index) => (
                    <TimelineItem
                        key={entry.id}
                        id={entry.id}
                        position={entry.position}
                        content={entry.content}
                        gapBottom={gaps[index] ?? MIN_GAP_PX}
                        dateLabel={dateLabels[index]}
                    />
                ))}
            </div>
        </div>
    );
}
